"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateHtmlAttributesFromPathProperties = exports.getNormalizedStrokeProperties = exports.getViewBox = exports.getViewBoxFromPath = exports.getRectangleCoordinates = exports.getArc = exports.getCoordinatesOfArcPoint = exports.generatePathFromCoordinates = exports.getRoundedPolygon = exports.getAngleBetweenSides = exports.getRegularPolygonCoordinates = void 0;
const constants_1 = require("../constants");
const svg_path_bounding_box_1 = require("svg-path-bounding-box");
function getRegularPolygonCoordinates(numberOfSides, radius, rotation) {
    const coordinates = [];
    for (let i = 0; i < numberOfSides; i++) {
        const angle = rotation ? (2 * Math.PI * i) / numberOfSides + rotation : (2 * Math.PI * i) / numberOfSides;
        coordinates.push({
            x: radius + radius * Math.sin(angle),
            y: radius - radius * Math.cos(angle),
        });
    }
    return coordinates;
}
exports.getRegularPolygonCoordinates = getRegularPolygonCoordinates;
function getAngleBetweenSides(startPoint, intersectionPoint, endPoint) {
    return (Math.atan2(intersectionPoint.y - startPoint.y, intersectionPoint.x - startPoint.x) -
        Math.atan2(intersectionPoint.y - endPoint.y, intersectionPoint.x - endPoint.x));
}
exports.getAngleBetweenSides = getAngleBetweenSides;
function getRoundedPolygon(coordinates, radius) {
    let path = '';
    if (coordinates.length >= 3) {
        const lengthOfSides = Math.sqrt(Math.pow(coordinates[0].x - coordinates[1].x, 2) + Math.pow(coordinates[0].y - coordinates[1].y, 2));
        // Angle between two sides.
        const angle = getAngleBetweenSides(coordinates[0], coordinates[1], coordinates[2]);
        radius = (Math.tan(angle / 2) * (lengthOfSides / 2) * radius) / 100; // converting radius relative to length of sides
        for (let i = 0; i < coordinates.length; i++) {
            const previousCoordinate = i >= 1 ? coordinates[i - 1] : coordinates[coordinates.length - 1];
            const currentCoordinate = coordinates[i];
            const nextCoordinate = i < coordinates.length - 1 ? coordinates[i + 1] : coordinates[0];
            // Let P(X,Y) is currentCoordinate, P1(X,Y) is previousCoordinate, P2(X,Y) nextCoordinate
            // Let C1(X,Y) and C2(X,Y) be 2 control points to draw Arc at the corners
            // angleBetweenSides = atan(PY - P1Y, PX - P1X) - atan(PY - P2Y, PX - P2X)
            const angleBetweenSides = getAngleBetweenSides(previousCoordinate, currentCoordinate, nextCoordinate);
            // segment is distance between current coordinate to control points
            // segment = PC1 = PC2 = radius / |tan(angle / 2)|
            const segment = radius / Math.tan(angleBetweenSides / 2);
            // C1X = PX - (PX - P1X) * PC1 / PP1
            // C1Y = PY - (PY - P1Y) * PC1 / PP1
            const firstControlPointX = currentCoordinate.x - ((currentCoordinate.x - previousCoordinate.x) * segment) / lengthOfSides;
            const firstControlPointY = currentCoordinate.y - ((currentCoordinate.y - previousCoordinate.y) * segment) / lengthOfSides;
            // C2X = PX - (PX - P2X) * PC2 / PP2
            // C2Y = PY - (PY - P2Y) * PC2/ PP2
            const secondControlPointX = currentCoordinate.x - ((currentCoordinate.x - nextCoordinate.x) * segment) / lengthOfSides;
            const secondControlPointY = currentCoordinate.y - ((currentCoordinate.y - nextCoordinate.y) * segment) / lengthOfSides;
            path += `L${firstControlPointX},
            ${firstControlPointY} A${radius} ${radius} 0 0 1 ${secondControlPointX} ${secondControlPointY} `;
        }
        path += 'Z'; // close the path
    }
    return path.replace('L', 'M'); // the first command is "M"
}
exports.getRoundedPolygon = getRoundedPolygon;
function generatePathFromCoordinates(coordinates) {
    let path = '';
    for (let index = 0; index < coordinates.length; index++) {
        const { x, y } = coordinates[index];
        path = `${path} ${index === 0 ? 'M' : 'L'}${x},${y}`;
    }
    return `${path} Z`;
}
exports.generatePathFromCoordinates = generatePathFromCoordinates;
function getCoordinatesOfArcPoint(center, radius, angle) {
    const angleInRadians = angle - Math.PI / 2; // calculate angle wrt X-axis
    return {
        x: center.x + radius * Math.cos(angleInRadians),
        y: center.y + radius * Math.sin(angleInRadians),
    };
}
exports.getCoordinatesOfArcPoint = getCoordinatesOfArcPoint;
function getArc(center, radius, startAngle, endAngle) {
    const end = getCoordinatesOfArcPoint(center, radius, endAngle);
    const largeArcFlag = endAngle - startAngle <= Math.PI ? '0' : '1';
    const arcPath = ['A', radius, radius, 0, largeArcFlag, 1, end.x, end.y].join(' ');
    return arcPath;
}
exports.getArc = getArc;
function getRectangleCoordinates(startPoint, width, height) {
    // consider ABCD is Rectangle
    return [
        { x: startPoint.x, y: startPoint.y },
        { x: startPoint.x + width, y: startPoint.y },
        { x: startPoint.x + width, y: startPoint.y + height },
        { x: startPoint.x, y: startPoint.y + height }, // Line CD
    ]; // Coordinates for rectangle
}
exports.getRectangleCoordinates = getRectangleCoordinates;
function getViewBoxDimensionIncludingStroke(boundingBox, strokeWidth) {
    const { x, y, width, height } = boundingBox;
    return `${x - strokeWidth} ${y - strokeWidth} ${width + 2 * strokeWidth} ${height + 2 * strokeWidth}`;
}
function getViewBoxFromPath(paths) {
    const singlePath = paths.join(' ');
    const path = new svg_path_bounding_box_1.Path(singlePath);
    const bBox = path.getBoundingBox();
    if (bBox) {
        const x = bBox.x1;
        const y = bBox.y1;
        const width = bBox.x2 - bBox.x1;
        const height = bBox.y2 - bBox.y1;
        return { x, y, width, height };
    }
    return undefined;
}
exports.getViewBoxFromPath = getViewBoxFromPath;
function getViewBox(params) {
    let viewBox;
    const { paths, strokeWidth = 0, dimensions } = params;
    if (paths) {
        viewBox = getViewBoxFromPath(paths) || { x: 0, y: 0, width: constants_1.SvgDefaults.viewBoxWidth, height: constants_1.SvgDefaults.viewBoxWidth };
    }
    else if (dimensions) {
        const { width, height } = dimensions;
        viewBox = { x: 0, y: 0, width, height };
    }
    return viewBox && getViewBoxDimensionIncludingStroke(viewBox, strokeWidth);
}
exports.getViewBox = getViewBox;
function getNormalizedStrokeProperties(stroke) {
    var _a;
    const strokeColor = (stroke === null || stroke === void 0 ? void 0 : stroke.color) || constants_1.SvgDefaults.stroke;
    const strokeWidth = (stroke === null || stroke === void 0 ? void 0 : stroke.width) === undefined ? constants_1.SvgDefaults.strokeWidth : stroke === null || stroke === void 0 ? void 0 : stroke.width;
    const lineCap = stroke === null || stroke === void 0 ? void 0 : stroke.lineCap;
    const lineJoin = stroke === null || stroke === void 0 ? void 0 : stroke.lineJoin;
    const dashArray = (_a = stroke === null || stroke === void 0 ? void 0 : stroke.dashArray) === null || _a === void 0 ? void 0 : _a.join();
    return { strokeColor, strokeWidth, lineCap, lineJoin, dashArray };
}
exports.getNormalizedStrokeProperties = getNormalizedStrokeProperties;
function generateHtmlAttributesFromPathProperties(pathProperties) {
    const { strokeColor, strokeWidth, lineCap, lineJoin, dashArray } = getNormalizedStrokeProperties(pathProperties.stroke);
    const fill = pathProperties.fill || constants_1.SvgDefaults.fillColor;
    const transform = pathProperties.transform;
    const pathHtmlAttributes = [`stroke="${strokeColor}"`, `stroke-width="${strokeWidth}"`, `fill="${fill}"`];
    if (lineCap) {
        pathHtmlAttributes.push(`stroke-linecap="${lineCap}"`);
    }
    if (lineJoin) {
        pathHtmlAttributes.push(`stroke-linejoin="${lineJoin}"`);
    }
    if (dashArray) {
        pathHtmlAttributes.push(`stroke-dasharray="${dashArray}"`);
    }
    if (transform) {
        pathHtmlAttributes.push(`transform="${transform}"`);
    }
    return pathHtmlAttributes.join(' ');
}
exports.generateHtmlAttributesFromPathProperties = generateHtmlAttributesFromPathProperties;
//# sourceMappingURL=utility.paths.js.map